import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, message } from 'antd';
import enUS from 'antd/locale/en_US';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
import { Layout } from './components/Layout';
import { Login } from './pages/Login';
import { CustomerLogin } from './pages/CustomerLogin';
import { CustomerChangePassword } from './pages/CustomerChangePassword';
import { Dashboard } from './pages/Dashboard';
import { CustomerDashboard } from './pages/CustomerDashboard';
import { CustomerPersonal } from './pages/CustomerPersonal';
import { CustomerPersonalDetail } from './pages/CustomerPersonalDetail';
import { CustomerBusiness } from './pages/CustomerBusiness';
import { CustomerBusinessDetail } from './pages/CustomerBusinessDetail';
import { CustomerBusinessFormPage } from './pages/CustomerBusinessForm';
import { CustomerTasks } from './pages/CustomerTasks';
import { CustomerFiles } from './pages/CustomerFiles';
import { CustomerBilling } from './pages/CustomerBilling';
import { CustomerTaskDetail } from './pages/CustomerTaskDetail';
import { CustomerLetterDetail } from './pages/CustomerLetterDetail';
import { CustomerDispatchDetail } from './pages/CustomerDispatchDetail';
import { CustomerQuotationDetail } from './pages/CustomerQuotationDetail';
import { CustomerInvoiceDetail } from './pages/CustomerInvoiceDetail';
import { CustomerLayout } from './components/CustomerLayout';
import { Products } from './pages/Products';
import { Inventory } from './pages/Inventory';
import { Contacts } from './pages/Contacts';
import { People } from './pages/People';
import { Customers } from './pages/Customers';
import { CustomerImport } from './pages/CustomerImport';
import { CustomerFormPage } from './pages/CustomerForm';
import { Businesses } from './pages/Businesses';
import { BusinessImport } from './pages/BusinessImport';
import { BusinessFormPage } from './pages/BusinessForm';
import { Agents } from './pages/Agents';
import { Teams } from './pages/Teams';
import { Staffs } from './pages/Staffs';
import { StaffFormPage } from './pages/StaffForm';
import { CalendarPage } from './pages/Calendar';
import { EventFormPage } from './pages/EventForm';
import { TaskTypes } from './pages/TaskTypes';
import { TaskTemplates } from './pages/TaskTemplates';
import { TaskTemplateFormPage } from './pages/TaskTemplateForm';
import { SubTasks } from './pages/SubTasks';
import { Tasks } from './pages/Tasks';
import { TaskFormPage } from './pages/TaskForm';
import { TaskDetailPage } from './pages/TaskDetail';
import { Letters } from './pages/Letters';
import { LetterFormPage } from './pages/LetterForm';
import { Dispatches } from './pages/Dispatches';
import { DispatchFormPage } from './pages/DispatchForm';
import { Files } from './pages/Files';
import { Reports } from './pages/Reports';
import { Users } from './pages/Users';
import { UserFormPage } from './pages/UserForm';
import { Roles } from './pages/Roles';
import { Permissions } from './pages/Permissions';
import { Quotations } from './pages/Quotations';
import { QuotationFormPage } from './pages/QuotationForm';
import { Invoices } from './pages/Invoices';
import { InvoiceFormPage } from './pages/InvoiceForm';
import { EInvoicePage } from './pages/EInvoice';
import { GeneralSettings } from './pages/settings/GeneralSettings';
import { CompanyProfilePage } from './pages/settings/CompanyProfile';
import { NumberingFormatsPage } from './pages/settings/NumberingFormats';
import { CurrencySettingsPage } from './pages/settings/CurrencySettings';
import { DeadlineSettingsPage } from './pages/settings/DeadlineSettings';
import { hasPermission } from './utils/permissions';
import './App.css';

type PermissionRouteProps = {
  permission: string;
  redirectTo: string;
  children: React.ReactNode;
};

const RequirePermission = ({ permission, redirectTo, children }: PermissionRouteProps): React.ReactElement | null => {
  const { user, loading } = useAuth();
  if (loading) {
    return null;
  }
  if (!hasPermission(user, permission)) {
    return <Navigate to={redirectTo} replace />;
  }
  return <>{children}</>;
};

function App() {
  useEffect(() => {
    // Configure message to appear at the top of the page
    message.config({
      top: 100, // Distance from top of viewport (increased to 100px to be more visible)
      duration: 5, // Display duration in seconds (increased to 5 seconds)
      maxCount: 5, // Maximum number of messages to display at once
      rtl: false,
    });
  }, []);

  return (
    <ConfigProvider locale={enUS}>
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/customer/login" element={<CustomerLogin />} />
            <Route path="/customer/change-password" element={<CustomerChangePassword />} />
            <Route path="/customer" element={<CustomerLayout />}>
              <Route index element={<Navigate to="/customer/dashboard" replace />} />
            <Route path="dashboard" element={<CustomerDashboard />} />
            <Route path="personal" element={<CustomerPersonal />} />
            <Route path="personal/detail" element={<CustomerPersonalDetail />} />
            <Route path="business" element={<CustomerBusiness />} />
            <Route path="files" element={<CustomerFiles />} />
            <Route path="businesses/:id" element={<CustomerBusinessDetail />} />
            <Route path="businesses/:id/edit" element={<CustomerBusinessFormPage />} />
            <Route path="tasks" element={<CustomerTasks />} />
            <Route path="billing" element={<CustomerBilling />} />
            <Route path="tasks/:id" element={<CustomerTaskDetail />} />
            <Route path="letters/:id" element={<CustomerLetterDetail />} />
            <Route path="dispatches/:id" element={<CustomerDispatchDetail />} />
            <Route path="quotation/:id" element={<CustomerQuotationDetail />} />
            <Route path="invoice/:id" element={<CustomerInvoiceDetail />} />
            <Route path="reports" element={<Reports />} />
          </Route>
            <Route path="/" element={<Layout />}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="people" element={<People />} />
              <Route path="products" element={<Products />} />
              <Route path="inventory" element={<Inventory />} />
              <Route path="contacts" element={<Contacts />} />
              <Route path="contacts/customers" element={<Contacts />} />
              <Route path="contacts/suppliers" element={<Contacts />} />
              <Route path="customers" element={<Customers />} />
              <Route
                path="customers/import"
                element={
                  <RequirePermission permission="personal.create" redirectTo="/customers">
                    <CustomerImport />
                  </RequirePermission>
                }
              />
              <Route
                path="customers/new"
                element={
                  <RequirePermission permission="personal.create" redirectTo="/customers">
                    <CustomerFormPage />
                  </RequirePermission>
                }
              />
              <Route
                path="customers/:id/edit"
                element={
                  <RequirePermission permission="personal.view" redirectTo="/customers">
                    <CustomerFormPage />
                  </RequirePermission>
                }
              />
              <Route path="businesses" element={<Businesses />} />
              <Route
                path="businesses/import"
                element={
                  <RequirePermission permission="business.create" redirectTo="/businesses">
                    <BusinessImport />
                  </RequirePermission>
                }
              />
              <Route
                path="businesses/new"
                element={
                  <RequirePermission permission="business.create" redirectTo="/businesses">
                    <BusinessFormPage />
                  </RequirePermission>
                }
              />
              <Route
                path="businesses/:id/edit"
                element={
                  <RequirePermission permission="business.view" redirectTo="/businesses">
                    <BusinessFormPage />
                  </RequirePermission>
                }
              />
              <Route path="agents" element={<Agents />} />
              <Route path="teams" element={<Teams />} />
              <Route path="staffs" element={<Staffs />} />
              <Route
                path="staffs/new"
                element={
                  <RequirePermission permission="staff.create" redirectTo="/staffs">
                    <StaffFormPage />
                  </RequirePermission>
                }
              />
              <Route
                path="staffs/:id/edit"
                element={
                  <RequirePermission permission="staff.view" redirectTo="/staffs">
                    <StaffFormPage />
                  </RequirePermission>
                }
              />
              <Route path="calendar" element={<CalendarPage />} />
              <Route
                path="calendar/events/new"
                element={
                  <RequirePermission permission="calendar.create" redirectTo="/calendar">
                    <EventFormPage />
                  </RequirePermission>
                }
              />
              <Route
                path="calendar/events/:id/edit"
                element={
                  <RequirePermission permission="calendar.view" redirectTo="/calendar">
                    <EventFormPage />
                  </RequirePermission>
                }
              />
              <Route path="task-types" element={<TaskTypes />} />
              <Route path="task-templates" element={<TaskTemplates />} />
              <Route
                path="task-templates/new"
                element={
                  <RequirePermission permission="task-templates.create" redirectTo="/task-templates">
                    <TaskTemplateFormPage />
                  </RequirePermission>
                }
              />
              <Route
                path="task-templates/:id/edit"
                element={
                  <RequirePermission permission="task-templates.view" redirectTo="/task-templates">
                    <TaskTemplateFormPage />
                  </RequirePermission>
                }
              />
              <Route path="sub-tasks" element={<SubTasks />} />
              <Route path="tasks" element={<Tasks />} />
              <Route
                path="tasks/new"
                element={
                  <RequirePermission permission="tasks.create" redirectTo="/tasks">
                    <TaskFormPage />
                  </RequirePermission>
                }
              />
              <Route
                path="tasks/:id/edit"
                element={
                  <RequirePermission permission="tasks.view" redirectTo="/tasks">
                    <TaskFormPage />
                  </RequirePermission>
                }
              />
              <Route path="tasks/:id" element={<TaskDetailPage />} />
              <Route path="letters" element={<Letters />} />
              <Route
                path="letters/new"
                element={
                  <RequirePermission permission="letters.create" redirectTo="/letters">
                    <LetterFormPage />
                  </RequirePermission>
                }
              />
              <Route
                path="letters/:id/edit"
                element={
                  <RequirePermission permission="letters.view" redirectTo="/letters">
                    <LetterFormPage />
                  </RequirePermission>
                }
              />
            <Route path="dispatches" element={<Dispatches />} />
            <Route
              path="dispatches/new"
              element={
                <RequirePermission permission="dispatches.create" redirectTo="/dispatches">
                  <DispatchFormPage />
                </RequirePermission>
              }
            />
            <Route
              path="dispatches/:id/edit"
              element={
                <RequirePermission permission="dispatches.view" redirectTo="/dispatches">
                  <DispatchFormPage />
                </RequirePermission>
              }
            />
            <Route path="files" element={<Files />} />
            <Route path="reports" element={<Reports />} />
            <Route path="users" element={<Users />} />
              <Route
                path="users/new"
                element={
                  <RequirePermission permission="staff.create" redirectTo="/users">
                    <UserFormPage />
                  </RequirePermission>
                }
              />
              <Route
                path="users/:id/edit"
                element={
                  <RequirePermission permission="staff.view" redirectTo="/users">
                    <UserFormPage />
                  </RequirePermission>
                }
              />
              <Route path="roles" element={<Roles />} />
              <Route path="permissions" element={<Permissions />} />
              <Route path="quotations" element={<Quotations />} />
              <Route
                path="quotations/new"
                element={
                  <RequirePermission permission="quotations.create" redirectTo="/quotations">
                    <QuotationFormPage />
                  </RequirePermission>
                }
              />
              <Route
                path="quotations/:id/edit"
                element={
                  <RequirePermission permission="quotations.view" redirectTo="/quotations">
                    <QuotationFormPage />
                  </RequirePermission>
                }
              />
              <Route path="invoices" element={<Invoices />} />
              <Route
                path="invoices/new"
                element={
                  <RequirePermission permission="invoices.create" redirectTo="/invoices">
                    <InvoiceFormPage />
                  </RequirePermission>
                }
              />
              <Route
                path="invoices/:id/edit"
                element={
                  <RequirePermission permission="invoices.view" redirectTo="/invoices">
                    <InvoiceFormPage />
                  </RequirePermission>
                }
              />
              <Route path="e-invoice" element={<EInvoicePage />} />
              <Route path="settings/general" element={<GeneralSettings />} />
              <Route path="settings/company" element={<CompanyProfilePage />} />
              <Route path="settings/numbering" element={<NumberingFormatsPage />} />
              <Route path="settings/currency" element={<CurrencySettingsPage />} />
              <Route path="settings/deadlines" element={<DeadlineSettingsPage />} />
            </Route>
          </Routes>
        </Router>
      </AuthProvider>
    </ConfigProvider>
  );
}

export default App;
